import { DatabaseChangeType } from '@databases/types/redux';

import { YJSChangeHandler } from '../types';

import { removeCommentThread } from './db/remove-comment-thread';
import { toggleLockStructure } from './db/toggle-lock-structure';
import { addEntry } from './entry/add-entry';
import { clearEntries } from './entry/clear-entries';
import { cloneEntries } from './entry/clone-entries';
import { deleteEntry } from './entry/delete-entry';
import { reorderEntries } from './entry/reorder-entries';
import { addField } from './field/add-field';
import { cloneFields } from './field/clone-fields';
import { deleteField } from './field/delete-field';
import { reorderField } from './field/reorder-field';
import { setFieldDescription } from './field/set-field-description';
import { setFieldId } from './field/set-field-id';
import { setFieldName } from './field/set-field-name';
import { setFieldWidth } from './field/set-field-width';
import { setMainFieldId } from './field/set-main-field-id';
import { setShowFieldDescription } from './field/set-show-field-description';
import { setAllowEdit } from './field-settings/set-allow-edit';
import { setBacklinkDisplayType } from './field-settings/set-backlink-display-type';
import { setCloudId } from './field-settings/set-cloud-id';
import { setContentField } from './field-settings/set-content-field';
import { setDatabaseId } from './field-settings/set-database-id';
import { setDefaultEnabled } from './field-settings/set-default-enabled';
import { setDisplayDetail } from './field-settings/set-display-detail';
import { setDisplayEntryOnPage } from './field-settings/set-display-entry-on-page';
import { setEntryFieldField } from './field-settings/set-entry-field-field';
import { setFieldSettingsMediaFormat } from './field-settings/set-field-settings-media-format';
import { setFieldSettingsMinimumOrMaximumFractionDigitsField } from './field-settings/set-field-settings-minimum-or-maximum-fraction-digits-field';
import { setFieldSettingsNumberFormat } from './field-settings/set-field-settings-number-format';
import { setFieldSettingsPrefixOrSuffixField } from './field-settings/set-field-settings-prefix-or-suffix-field';
import { setFieldSettingsSeparatorField } from './field-settings/set-field-settings-separator-field';
import { setFieldType } from './field-settings/set-field-type';
import { setIncludeVersionComment } from './field-settings/set-include-version-comment';
import { setIsCreatable } from './field-settings/set-is-creatable';
import { setIsMulti } from './field-settings/set-is-multi';
import { setIsParentPageSourceDatabase } from './field-settings/set-is-parent-page-source-database';
import { setJiraField } from './field-settings/set-jira-field';
import { setJiraServerId } from './field-settings/set-jira-server-id';
import { setMentionNotificationEnabled } from './field-settings/set-mention-notification-enabled';
import { setNewLines } from './field-settings/set-new-lines';
import { setPageLocation } from './field-settings/set-page-location';
import { setPageSubType } from './field-settings/set-page-sub-type';
import { setPageTemplate } from './field-settings/set-page-template';
import { setParentPage } from './field-settings/set-parent-page';
import { setParentPageOption } from './field-settings/set-parent-page-option';
import { setResolve } from './field-settings/set-resolve';
import { setSelectOptions } from './field-settings/set-select-options';
import { addValueCommentThread } from './value/add-value-comment-thread';
import { bulkSetUndefinedValues } from './value/bulk-set-undefined-values';
import { createFieldAndSetValue } from './value/create-field-and-set-value';
import { decrementValueCommentThreadCommentCount } from './value/decrement-value-comment-thread-comment-count';
import { incrementValueCommentThreadCommentCount } from './value/increment-value-comment-thread-comment-count';
import { incrementValueCommentThreadVersionNumber } from './value/increment-value-comment-thread-version-number';
import { setValue } from './value/set-value';
import { setValueCommentThread } from './value/set-value-comment-thread';
import { setValueCommentThreadCommentCount } from './value/set-value-comment-thread-comment-count';
import { setValueCommentThreadResolved } from './value/set-value-comment-thread-resolved';
import { addSavedView } from './view/add-saved-view';
import { bulkApplySavedViewChanges } from './view/bulk-apply-saved-view-changes';
import { deleteSavedView } from './view/delete-saved-view';
import { removeSavedViewCalculation } from './view/remove-saved-view-calculation';
import { reorderSavedView } from './view/reorder-saved-view';
import { setDefaultSavedView } from './view/set-default-saved-view';
import { setSavedView } from './view/set-saved-view';
import { setSavedViewCalculations } from './view/set-saved-view-calculations';
import { updateSavedViewName } from './view/update-saved-view-name';

export type YjsCallbackHandlerMap = {
  [K in DatabaseChangeType]?: YJSChangeHandler<K>;
};

/**
 * Map of change types to their corresponding handlers.
 * To add a new handler,
 */
export const yjsCallbackHandlerMap: YjsCallbackHandlerMap = {
  /**
   * DB
   */
  [DatabaseChangeType.TOGGLE_LOCK_STRUCTURE]: toggleLockStructure,

  /**
   * Entry
   */
  [DatabaseChangeType.ADD_ENTRY]: addEntry,
  [DatabaseChangeType.CLEAR_ENTRIES]: clearEntries,
  [DatabaseChangeType.CLONE_ENTRIES]: cloneEntries,
  [DatabaseChangeType.DELETE_ENTRY]: deleteEntry,
  [DatabaseChangeType.REORDER_ENTRIES]: reorderEntries,
  [DatabaseChangeType.SET_DISPLAY_ENTRY_ON_PAGE]: setDisplayEntryOnPage,
  [DatabaseChangeType.SET_ENTRY_FIELD_FIELD]: setEntryFieldField,

  /**
   * Field
   */
  [DatabaseChangeType.ADD_FIELD]: addField,
  [DatabaseChangeType.CLONE_FIELDS]: cloneFields,
  [DatabaseChangeType.DELETE_FIELD]: deleteField,
  [DatabaseChangeType.REORDER_FIELD]: reorderField,
  [DatabaseChangeType.SET_FIELD_DESCRIPTION]: setFieldDescription,
  [DatabaseChangeType.SET_FIELD_ID]: setFieldId,
  [DatabaseChangeType.SET_FIELD_NAME]: setFieldName,
  [DatabaseChangeType.SET_FIELD_TYPE]: setFieldType,
  [DatabaseChangeType.SET_FIELD_WIDTH]: setFieldWidth,
  [DatabaseChangeType.SET_MAIN_FIELD_ID]: setMainFieldId,
  [DatabaseChangeType.SET_SHOW_FIELD_DESCRIPTION]: setShowFieldDescription,

  /**
   * Field Settings
   */
  [DatabaseChangeType.SET_ALLOW_EDIT]: setAllowEdit,
  [DatabaseChangeType.SET_BACKLINK_DISPLAY_TYPE]: setBacklinkDisplayType,
  [DatabaseChangeType.SET_CLOUD_ID]: setCloudId,
  [DatabaseChangeType.SET_CONTENT_FIELD]: setContentField,
  [DatabaseChangeType.SET_DATABASE_ID]: setDatabaseId,
  [DatabaseChangeType.SET_DEFAULT_ENABLED]: setDefaultEnabled,
  [DatabaseChangeType.SET_DISPLAY_DETAIL]: setDisplayDetail,
  [DatabaseChangeType.SET_FIELD_SETTINGS_MINIMUM_OR_MAXIMUM_FRACTION_DIGITS_FIELD]:
    setFieldSettingsMinimumOrMaximumFractionDigitsField,
  [DatabaseChangeType.SET_FIELD_SETTINGS_PREFIX_OR_SUFFIX_FIELD]:
    setFieldSettingsPrefixOrSuffixField,
  [DatabaseChangeType.SET_FIELD_SETTINGS_NUMBER_FORMAT]: setFieldSettingsNumberFormat,
  [DatabaseChangeType.SET_FIELD_SETTINGS_MEDIA_FORMAT]: setFieldSettingsMediaFormat,
  [DatabaseChangeType.SET_FIELD_SETTINGS_SEPARATOR_FIELD]: setFieldSettingsSeparatorField,
  [DatabaseChangeType.SET_INCLUDE_VERSION_COMMENT]: setIncludeVersionComment,
  [DatabaseChangeType.SET_IS_CREATABLE]: setIsCreatable,
  [DatabaseChangeType.SET_IS_MULTI]: setIsMulti,
  [DatabaseChangeType.SET_IS_PARENT_PAGE_SOURCE_DATABASE]: setIsParentPageSourceDatabase,
  [DatabaseChangeType.SET_JIRA_FIELD]: setJiraField,
  [DatabaseChangeType.SET_JIRA_SERVER_ID]: setJiraServerId,
  [DatabaseChangeType.SET_MENTION_NOTIFICATION_ENABLED]: setMentionNotificationEnabled,
  [DatabaseChangeType.SET_NEWLINES]: setNewLines,
  [DatabaseChangeType.SET_PAGE_LOCATION]: setPageLocation,
  [DatabaseChangeType.SET_PAGE_TEMPLATE]: setPageTemplate,
  [DatabaseChangeType.SET_PAGE_SUB_TYPE]: setPageSubType,
  [DatabaseChangeType.SET_PARENT_PAGE_OPTION]: setParentPageOption,
  [DatabaseChangeType.SET_PARENT_PAGE]: setParentPage,
  [DatabaseChangeType.SET_RESOLVE]: setResolve,
  [DatabaseChangeType.SET_SELECT_OPTIONS]: setSelectOptions,

  /**
   * Value
   */
  [DatabaseChangeType.ADD_VALUE_COMMENT_THREAD]: addValueCommentThread,
  [DatabaseChangeType.REMOVE_VALUE_COMMENT_THREAD]: removeCommentThread,
  [DatabaseChangeType.BULK_SET_UNDEFINED_VALUES]: bulkSetUndefinedValues,
  [DatabaseChangeType.SET_VALUE]: setValue,
  [DatabaseChangeType.CREATE_FIELD_AND_SET_VALUE]: createFieldAndSetValue,
  [DatabaseChangeType.SET_VALUE_COMMENT_THREAD]: setValueCommentThread,
  [DatabaseChangeType.SET_VALUE_COMMENT_THREAD_RESOLVED]: setValueCommentThreadResolved,
  [DatabaseChangeType.SET_VALUE_COMMENT_THREAD_COMMENT_COUNT]:
    setValueCommentThreadCommentCount,
  [DatabaseChangeType.INCREMENT_VALUE_COMMENT_THREAD_COMMENT_COUNT]:
    incrementValueCommentThreadCommentCount,
  [DatabaseChangeType.DECREMENT_VALUE_COMMENT_THREAD_COMMENT_COUNT]:
    decrementValueCommentThreadCommentCount,
  [DatabaseChangeType.INCREMENT_VALUE_COMMENT_THREAD_VERSION_NUMBER]:
    incrementValueCommentThreadVersionNumber,

  /**
   * View
   */
  [DatabaseChangeType.ADD_SAVED_VIEW]: addSavedView,
  [DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES]: bulkApplySavedViewChanges,
  [DatabaseChangeType.DELETE_SAVED_VIEW]: deleteSavedView,
  [DatabaseChangeType.REMOVE_SAVED_VIEW_CALCULATION]: removeSavedViewCalculation,
  [DatabaseChangeType.REORDER_SAVED_VIEW]: reorderSavedView,
  [DatabaseChangeType.SET_DEFAULT_SAVED_VIEW]: setDefaultSavedView,
  [DatabaseChangeType.SET_SAVED_VIEW]: setSavedView,
  [DatabaseChangeType.SET_SAVED_VIEW_CALCULATIONS]: setSavedViewCalculations,
  [DatabaseChangeType.UPDATE_SAVED_VIEW_NAME]: updateSavedViewName,
} as const;
